<template>
  <div class="open-funnel-modal">
    <template v-if="stages?.length">
      <Tabs v-model="activeStage" class="open-funnel-modal__stages">
        <Tab v-for="stage in stages" :key="stage.value" :name="stage.value" :title="stage.title" />
      </Tabs>
      <p v-if="currentStage?.description" class="open-funnel-modal__stage-description">
        {{ currentStage.description }}
      </p>
    </template>

    <button
      v-for="option in visibleOptions"
      :key="option.value"
      type="button"
      class="open-funnel-modal__option"
      @click="select(option.value)"
    >
      <div class="open-funnel-modal__option-icon">
        <BaseIcon :name="option.icon" size="18" />
      </div>
      <div class="open-funnel-modal__option-content">
        <span class="open-funnel-modal__option-title">{{ option.title }}</span>
        <span class="open-funnel-modal__option-description">{{ option.description }}</span>
      </div>
      <BaseIcon name="arrow-right" size="14" class="open-funnel-modal__option-arrow" />
    </button>
  </div>
</template>

<script>
  import { computed, getCurrentInstance, ref } from 'vue';

  import Tab from '@/components/partials/Tabs/Tab.vue';
  import Tabs from '@/components/partials/Tabs/Tabs.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';

  export default {
    name: 'OpenFunnelModal',
    components: {
      BaseIcon,
      Tab,
      Tabs,
    },
    props: {
      modalId: { type: Number, required: false },
      onSelect: { type: Function, required: true },
      /**
       * Optional label overrides (e.g. proposal create flow).
       * Falls back to form_card.* funnel copy.
       */
      labels: {
        type: Object,
        default: null,
      },
      /**
       * Optional quote-type tabs shown above the choices:
       * `{ value, title, description, options: [{ value, icon, title, description }] }`.
       */
      stages: {
        type: Array,
        default: null,
      },
      defaultStage: {
        type: String,
        default: null,
      },
    },
    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;

      const activeStage = ref(props.defaultStage || props.stages?.[0]?.value || '');

      const currentStage = computed(
        () => props.stages?.find((stage) => stage.value === activeStage.value) || null
      );

      const visibleOptions = computed(() => {
        if (currentStage.value) return currentStage.value.options;
        return [
          {
            value: 'normal',
            icon: 'external-link',
            title: props.labels?.normalTitle || $t('form_card.open_normal'),
            description: props.labels?.normalDescription || $t('form_card.open_normal_description'),
          },
          {
            value: 'extended',
            icon: 'cubes',
            title: props.labels?.extendedTitle || $t('form_card.open_extended'),
            description:
              props.labels?.extendedDescription || $t('form_card.open_extended_description'),
          },
        ];
      });

      const select = (value) => {
        props.onSelect(value);
      };

      return { activeStage, currentStage, visibleOptions, select };
    },
  };
</script>

<style scoped lang="scss">
  .open-funnel-modal {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;

    &__stages {
      :deep(.tabs__headers) {
        display: flex;
        width: 100%;
        align-self: stretch;
      }

      :deep(.tabs__header) {
        flex: 1;
        justify-content: center;
      }

      :deep(.tabs__content) {
        display: none;
      }
    }

    &__stage-description {
      @include fontSize('sm');
      margin: 0;
      color: var(--slate-500);
    }

    &__option {
      display: flex;
      align-items: center;
      gap: 1rem;
      width: 100%;
      padding: 1rem;
      background: white;
      border: 1px solid var(--slate-200);
      border-radius: 0.5rem;
      cursor: pointer;
      text-align: left;
      transition:
        border-color 0.15s,
        background-color 0.15s;

      @include hover {
        border-color: var(--brand-300);
        background: var(--brand-50);
      }
    }

    &__option-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      flex-shrink: 0;
      border-radius: 0.5rem;
      background: var(--slate-100);
      color: var(--slate-600);
    }

    &__option-content {
      display: flex;
      flex-direction: column;
      gap: 0.125rem;
      flex: 1;
      min-width: 0;
    }

    &__option-title {
      font-weight: 600;
      color: var(--slate-900);
    }

    &__option-description {
      @include fontSize('sm');
      color: var(--slate-500);
    }

    &__option-arrow {
      color: var(--slate-400);
      flex-shrink: 0;
    }
  }
</style>
